// IndexedDB storage behind the video library (`src/lib/sources/library.js`). `handles` and `recent_files` are the pre-library stores, read once by the library's migration and then emptied.
const DB_NAME = 'oylo-video-file-handle'
const DB_VERSION = 3
const LEGACY_CURRENT = 'handles'
const LEGACY_RECENT = 'recent_files'
const ENTRIES = 'library'
const META = 'meta'

let dbInstance = null
let dbInitPromise = null

async function openDatabase() {
	if (dbInstance) return dbInstance
	if (dbInitPromise) return dbInitPromise

	dbInitPromise = new Promise((resolve, reject) => {
		const request = indexedDB.open(DB_NAME, DB_VERSION)

		request.onerror = () => {
			dbInitPromise = null
			reject(request.error)
		}

		// Another tab holds an older version open: fail now rather than wait for it, so library callers settle.
		request.onblocked = () => {
			dbInitPromise = null
			reject(
				new Error(
					'The video library is open in another Oylo tab running an older version. Close other Oylo tabs and reload.'
				)
			)
		}

		request.onsuccess = () => {
			const db = request.result
			const forget = () => {
				if (dbInstance === db) dbInstance = null
				dbInitPromise = null
			}
			db.onclose = forget
			// A newer tab is upgrading: let it. `close()` fires no `close` event.
			db.onversionchange = () => {
				db.close()
				forget()
			}
			dbInstance = db
			resolve(db)
		}

		request.onupgradeneeded = event => {
			const db = event.target.result
			for (const name of [LEGACY_CURRENT, LEGACY_RECENT, META]) {
				if (!db.objectStoreNames.contains(name)) db.createObjectStore(name)
			}
			if (!db.objectStoreNames.contains(ENTRIES)) db.createObjectStore(ENTRIES, { keyPath: 'id' })
		}
	})

	return dbInitPromise
}

const done = request =>
	new Promise((resolve, reject) => {
		request.onsuccess = () => resolve(request.result)
		request.onerror = () => reject(request.error)
	})

const committed = tx =>
	new Promise((resolve, reject) => {
		tx.oncomplete = () => resolve()
		tx.onerror = () => reject(tx.error)
	})

async function read(store, fn) {
	const db = await openDatabase()
	return done(fn(db.transaction(store, 'readonly').objectStore(store)))
}

async function write(stores, fn) {
	const db = await openDatabase()
	const tx = db.transaction(stores, 'readwrite')
	fn(tx)
	return committed(tx)
}

export const idbLibraryStorage = {
	all: () => read(ENTRIES, s => s.getAll()),
	put: entry => write(ENTRIES, tx => tx.objectStore(ENTRIES).put(entry)),
	delete: id => write(ENTRIES, tx => tx.objectStore(ENTRIES).delete(id)),
	getMeta: key => read(META, s => s.get(key)),
	setMeta: (key, value) => write(META, tx => tx.objectStore(META).put(value, key)),
	async legacy() {
		const current = await read(LEGACY_CURRENT, s => s.get('current'))
		const recent = await read(LEGACY_RECENT, s => s.get('list'))
		return { current: current ?? null, recent: recent ?? [] }
	},
	clearLegacy: () =>
		write([LEGACY_CURRENT, LEGACY_RECENT], tx => {
			tx.objectStore(LEGACY_CURRENT).clear()
			tx.objectStore(LEGACY_RECENT).clear()
		}),
}

export function hasFileSystemAccessAPI() {
	return 'showOpenFilePicker' in window
}
